import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * AvatarHintTooltip — wraps an avatar upload trigger and reveals a small
 * synthwave-styled hint card on hover with the recommended upload specs.
 *
 * The popup is rendered via a portal into <body> so it escapes any
 * `overflow-hidden` ancestor (e.g. rounded portrait cards).
 */
const TOOLTIP_TEXT = [
  "300×300 px square image",
  "PNG, WebP, or JPG",
  "Larger images auto-resized/cropped",
  "Transparency supported for promotion backgrounds",
  "Max 3 MB",
];

export default function AvatarHintTooltip({ children, className = "" }) {
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [show, setShow] = useState(false);

  useLayoutEffect(() => {
    if (!show || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Anchor the tooltip above the trigger, centered horizontally. Clamp to viewport.
    const cx = rect.left + rect.width / 2;
    const cy = rect.top;
    setPos({ x: cx, y: cy });
  }, [show]);

  const showTip = () => setShow(true);
  const hideTip = () => setShow(false);

  return (
    <div
      ref={triggerRef}
      className={`inline-block ${className}`}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
    >
      {children}
      {show && pos && createPortal(
        <div
          data-testid="avatar-hint-tooltip"
          role="tooltip"
          style={{
            position: "fixed",
            left: `${pos.x}px`,
            top: `${pos.y - 12}px`,
            transform: "translate(-50%, -100%)",
            maxWidth: "16rem",
          }}
          className="pointer-events-none z-[9999] w-64 rounded-md border border-[#00F0FF]/60 bg-[#0A051A]/95 backdrop-blur px-3 py-2 text-[11px] leading-relaxed font-mono text-[#E7E1FF] shadow-[0_0_16px_#00F0FF55]"
        >
          <div className="mb-1 text-[10px] uppercase tracking-widest text-[#00F0FF]">Recommended</div>
          <ul className="space-y-0.5">
            {TOOLTIP_TEXT.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
